import { useState, useEffect } from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from '@mui/material/Modal';
import { useForm } from 'react-hook-form';
import { Delete } from '@mui/icons-material';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputTextArea } from '../../../form-components/FormInputTextArea';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import { createProgramSession } from '../../../api/program';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { imageValidation } from '../../../utils/methods';
import { getProgramSessionId } from '../../../api/program';
import { updateProgramSession } from '../../../api/program';
import { options } from '../../../constants/constants';

const BodySessionCreateModal = ({ type, open, setOpen, data, getSession, selectedSession }) => {
  let content;
  let backgroundStyle;

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '147px',
    width: '188px',
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
  });

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '706px',
    height: '680px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: '30px 40px 30px 40px',
    borderRadius: '10px',
  };

  const toast = useToast();
  const { user } = useSelector(state => state.user);
  const { t } = useTranslation();
  const [imgBox, setImgBox] = useState(null);
  const { handleSubmit, control, watch, setError, reset, setValue } = useForm({
    defaultValues: {},
  });

  const [imgUrl, setImgUrl] = useState(null);

  const getSessionId = async () => {
    try {
      const dataSession = await getProgramSessionId(selectedSession, user.token);
      const transformedData = {
        id: dataSession.sessionId,
        name: dataSession.sessionName,
        description: dataSession.description,
        timer: dataSession.timer,
      };
      setImgBox(dataSession.image);

      for (const [key, value] of Object.entries(transformedData)) {
        setValue(`${key}`, value);
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleUploadTicketImages = async e => {
    try {
      const loadedImage = await imageValidation(e.target.files[0]);
      setImgUrl(loadedImage);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const submit = async values => {
    if (!values.name) {
      return setError('name', {
        type: 'custom',
        message: 'Please input member name',
      });
    }

    const request = {
      programId: data.id,
      name: values.name,
      description: values.description,
      image: imgUrl,
      timer: Number(values.timer),
    };
    const requestUpdate = {
      name: values.name,
      description: values.description,
      image: imgUrl,
      timer: Number(values.timer),
    };

    try {
      if (type === 'create') {
        await createProgramSession(request, user.token).then(() => {
          toast.success(t('Create program session success'));
          getSession(data.id);
        });
      } else {
        await updateProgramSession(requestUpdate, user.token, values.id).then(() => {
          toast.success(t('Update program session success'));
          getSession(data.id);
        });
      }
      reset();
      handleClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  if (imgUrl === null) {
    // No image URL provided
    if (imgBox != null) {
      // Use imgBox if available
      backgroundStyle = `url(${imgBox}) no-repeat center/cover`; // Use imgBox as background
    } else {
      // Default to CloudUploadIcon
      content = <CloudUploadIcon color="secondary" />;
      backgroundStyle = '#F1F1F1'; // Fallback background color
    }
  } else {
    backgroundStyle = `url(${(window.URL || window.webkitURL).createObjectURL(imgUrl)}) no-repeat center/cover`;
  }

  const handleClose = () => {
    setOpen(false);
    if (type === 'create') {
      reset();
      setImgUrl(null);
      setImgBox(null);
    } else {
      getSessionId();
    }
  };

  useEffect(() => {
    if (selectedSession) {
      getSessionId();
    }
  }, [selectedSession]);

  return (
    <div style={{ display: 'flex' }}>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={modalStyle}>
          <Box>
            <Typography
              sx={{
                fontSize: '18px',
              }}>
              {t('Create/Edit New Session')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '450px',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: '20px',
                mt: '30px',
              }}>
              <Typography>{t('Session Name')}</Typography>
              <FormInputText
                control={control}
                onMouseOver={e => (e.target.style.cursor = 'pointer')}
                color="info"
                sx={{
                  width: '280px',
                  height: '30px',
                  '& .MuiInputBase-root': {
                    borderRadius: '8px',
                  },
                }}
                variant="outlined"
                name="name"
                size="small"
                placeholder={t('Please enter a session name (within 10 characters)')}
                rules={{ required: true }}></FormInputText>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '450px',
                justifyContent: 'space-between',
                mb: '10px',
              }}>
              <Typography>{t('Session Description')}</Typography>
              <FormInputTextArea
                control={control}
                minRows={8}
                style={{
                  borderRadius: '4px',
                  width: '280px',
                  height: '229px',
                  color: '#3A3A3A',
                  padding: '12px 13px 10px 11px',
                  fontSize: '13px',
                  fontWeight: '500',
                  border: '1px solid #AEAEAEB5',
                  color: '#AEAEAEB',
                  borderRadius: '8px',
                }}
                placeholder={t('Please enter a description')}
                name="description"
                rules={{ required: true }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '570px',
              }}>
              <Typography>{t('Upload Image')}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '400px',
                  mb: '20px',
                }}>
                <Box
                  sx={{
                    position: 'relative',
                  }}>
                  <Button
                    sx={{
                      position: 'relative',
                      width: '188px',
                      height: '147px',
                      background: backgroundStyle,
                      ':hover': {
                        bgcolor: '#F1F1F1',
                        border: '1px solid #0F7F85',
                      },
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}>
                    <VisuallyHiddenInput name="imgUrl" onChange={handleUploadTicketImages} type="file" />
                    {content}
                  </Button>
                  <Delete
                    onClick={() => {
                      setImgUrl(null);
                    }}
                    sx={{
                      zIndex: '1000',
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                    }}></Delete>
                </Box>
                <Typography
                  sx={{
                    display: 'flex',
                    width: '200px',
                  }}>
                  {t(
                    '[Image upload information] Size 800 * 600px, 4:3 ratio is recommended. Only jpg/png files can be registered',
                  )}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '450px',
              }}>
              <Typography>{t('Timer settings (seconds)')}</Typography>
              <FormInputDropdown
                control={control}
                sx={{
                  width: '280px',
                }}
                name="timer"
                options={options}
                staticValue={watch('timer')}
                disabledItem={<MenuItem disabled>{t('Select Second')}</MenuItem>}
              />
            </Box>
            <hr></hr>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
                paddingTop: '10px',
              }}>
              <Box>
                <Button
                  sx={{
                    marginRight: '10px',
                    border: '1px solid #0F7F85 ',
                    borderRadius: '8px',
                  }}
                  onClick={() => {
                    handleClose();
                    setImgUrl(null);
                  }}>
                  {t('CANCEL')}
                </Button>
                <Button
                  sx={{
                    background: '#0F7F85',
                    '&:hover': { backgroundColor: '#0F7F85' },
                    borderRadius: '4.88px',
                    ml: 'auto',
                    color: 'white',
                    borderRadius: '8px',
                  }}
                  onClick={handleSubmit(submit)}>
                  {t('Save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};
export default BodySessionCreateModal;
